import React, { useState, useEffect } from 'react';
import { Settings2, Loader2, Save, ChevronDown, ChevronUp, Ship, Plane, BatteryCharging, Percent } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { sea_lcl: Ship, air_general: Plane, air_battery: BatteryCharging };

export default function RateEditor({ token }) {
  const [open, setOpen] = useState(false);
  const [rates, setRates] = useState(null);
  const [labels, setLabels] = useState({});
  const [units, setUnits] = useState({});
  const [fx, setFx] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open || rates) return;
    Promise.all([
      fetch(`${API}/api/sourcing/freight-rates`, { headers }).then((r) => r.json()),
      fetch(`${API}/api/sourcing/fx-settings`, { headers }).then((r) => r.json()),
    ]).then(([fr, fs]) => {
      if (fr.success) { setRates(fr.data); setLabels(fr.labels); setUnits(fr.units); }
      if (fs.success) setFx({ markup_pct: (fs.data.markup_pct * 100).toFixed(1), ghs_markup_rmb: fs.data.ghs_markup_rmb });
    }).catch(() => toast.error('Could not load rates'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/sourcing/freight-rates`, { method: 'PUT', headers, body: JSON.stringify(rates) }),
        fetch(`${API}/api/sourcing/fx-settings`, { method: 'PUT', headers, body: JSON.stringify({ markup_pct: Number(fx.markup_pct) / 100, ghs_markup_rmb: Number(fx.ghs_markup_rmb) }) }),
      ]);
      const [d1, d2] = await Promise.all([r1.json(), r2.json()]);
      if (!r1.ok) throw new Error(d1.detail || 'Failed to save freight rates');
      if (!r2.ok) throw new Error(d2.detail || 'Failed to save FX settings');
      setRates(d1.data);
      toast.success('Rates updated — live on the portal immediately');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6" data-testid="rate-editor">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="rate-editor-toggle">
        <span className="flex items-center gap-2 font-bold text-gray-900"><Settings2 className="w-5 h-5 text-orange-600" /> Rates & Settings <span className="text-xs font-normal text-gray-500 hidden sm:inline">— freight per CBM/kg and currency markup buffer</span></span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          {!rates || !fx ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div> : (
            <>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Freight rates (USD)</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.keys(rates).map((k) => { const Icon = ICONS[k] || Ship; return (
                  <label key={k} className="block bg-gray-50 border border-gray-200 rounded-xl p-3">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><Icon className="w-4 h-4 text-orange-600" /> {labels[k] || k}</span>
                    <div className="mt-2 flex items-center gap-1">
                      <span className="text-gray-500 text-sm">$</span>
                      <input type="number" min="0" step="0.5" value={rates[k]} onChange={(e) => setRates({ ...rates, [k]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold focus:border-orange-500 focus:outline-none" data-testid={`rate-input-${k}`} />
                      <span className="text-gray-500 text-sm whitespace-nowrap">/ {units[k]}</span>
                    </div>
                  </label>
                ); })}
              </div>

              <p className="text-xs font-semibold text-gray-500 uppercase mt-5 mb-2">Exchange-rate markup buffer</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800"><Percent className="w-4 h-4 text-orange-600" /> Buffer on all currencies</span>
                  <div className="mt-2 flex items-center gap-1">
                    <input type="number" min="0" max="50" step="0.5" value={fx.markup_pct} onChange={(e) => setFx({ ...fx, markup_pct: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold focus:border-orange-500 focus:outline-none" data-testid="fx-markup-pct-input" />
                    <span className="text-gray-500 text-sm">%</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">Customers get this % less foreign currency per RMB than mid-market.</p>
                </label>
                <label className="block bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">GH₵ Ghana Cedi — absolute markdown</span>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-gray-500 text-sm">¥</span>
                    <input type="number" min="0" max="1" step="0.01" value={fx.ghs_markup_rmb} onChange={(e) => setFx({ ...fx, ghs_markup_rmb: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold focus:border-orange-500 focus:outline-none" data-testid="fx-ghs-markup-input" />
                    <span className="text-gray-500 text-sm whitespace-nowrap">below XE</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">e.g. XE 0.58 RMB/GH₵ − 0.03 = 0.55 shown on the website.</p>
                </label>
              </div>

              <div className="mt-4 flex justify-end">
                <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl" data-testid="rate-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save rates
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
